import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import type { LiveState, Race, Runner, Station, TimingPoint } from '../types';

export interface RaceDetail {
  race: Race;
  timingPoints: TimingPoint[];
  stations: Station[];
}

export const raceKey = (raceId: string) => ['race', raceId] as const;
export const liveKey = (raceId: string) => ['live', raceId] as const;
export const runnersKey = (raceId: string) => ['runners', raceId] as const;

/** Who's signed in; `admin` sees every race and manages directors. */
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api<{ sub: string; admin: boolean }>('/me'), staleTime: 5 * 60_000 });
}

export function useRace(raceId: string) {
  return useQuery({ queryKey: raceKey(raceId), queryFn: () => api<RaceDetail>(`/races/${raceId}`) });
}

/** The dashboard poll. */
export function useLive(raceId: string, intervalMs = 4000) {
  return useQuery({
    queryKey: liveKey(raceId),
    queryFn: () => api<LiveState>(`/races/${raceId}/live`),
    refetchInterval: intervalMs,
    refetchIntervalInBackground: false,
  });
}

export function useRunners(raceId: string, q: string, missingOnly: boolean) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (missingOnly) params.set('missingDescription', 'true');
  return useQuery({
    queryKey: [...runnersKey(raceId), q, missingOnly],
    queryFn: () => api<{ runners: Runner[] }>(`/races/${raceId}/runners?${params}`),
    placeholderData: (prev) => prev,
  });
}
